import React, { useState, useEffect } from 'react';
import ApiClient from '../../utils/ApiClient';

const AuditDetailModal = ({ auditSessionId, onClose }) => {
  const [auditDetail, setAuditDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('matched');

  useEffect(() => {
    fetchAuditDetail();
  }, [auditSessionId]);

  const fetchAuditDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ApiClient.get(`/api/inventory/audits/${auditSessionId}`);

      if (response.data.success) {
        setAuditDetail(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load audit details');
      }
    } catch (err) {
      setError('Failed to load audit details: ' + err.message);
      console.error('Error fetching audit details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderItemsTable = (items, type) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No {type} items
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Identifier
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ASIN
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                FNSKU
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Seller SKU
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Condition
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.identifier || 'N/A'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {item.asin || 'N/A'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {item.fnsku || 'N/A'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {item.sellerSku || 'N/A'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {item.location || 'N/A'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                  {item.quantity || 0}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                  {item.skuCondition !== null && item.skuCondition !== undefined
                    ? item.skuCondition
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white mb-10">
        {/* Header */}
        <div className="flex justify-between items-center pb-4 border-b">
          <h3 className="text-2xl font-bold text-gray-900">
            Audit Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="mt-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading audit details...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : auditDetail ? (
            <>
              {/* Summary Section */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Audit Session ID</p>
                  <p className="text-lg font-semibold text-gray-900">{auditDetail.auditSessionId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="text-lg font-semibold text-gray-900">{auditDetail.location}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Audited By</p>
                  <p className="text-lg font-semibold text-gray-900">{auditDetail.auditedBy || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Audit Date</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatDate(auditDetail.auditDate)}
                  </p>
                </div>
              </div>

              {/* Count Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-blue-600 font-medium">System Count</p>
                  <p className="text-2xl font-bold text-blue-900">{auditDetail.systemCount}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-purple-600 font-medium">Scanned Count</p>
                  <p className="text-2xl font-bold text-purple-900">{auditDetail.scannedCount}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-green-600 font-medium">Matched</p>
                  <p className="text-2xl font-bold text-green-900">{auditDetail.matchedCount}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-red-600 font-medium">Missing</p>
                  <p className="text-2xl font-bold text-red-900">{auditDetail.missingCount}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-orange-600 font-medium">Extra</p>
                  <p className="text-2xl font-bold text-orange-900">{auditDetail.extraCount}</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('matched')}
                    className={`${
                      activeTab === 'matched'
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Matched Items ({auditDetail.matchedItems?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('missing')}
                    className={`${
                      activeTab === 'missing'
                        ? 'border-red-500 text-red-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Missing Items ({auditDetail.missingItems?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('extra')}
                    className={`${
                      activeTab === 'extra'
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Extra Items ({auditDetail.extraItems?.length || 0})
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="max-h-96 overflow-y-auto">
                {activeTab === 'matched' && renderItemsTable(auditDetail.matchedItems, 'matched')}
                {activeTab === 'missing' && renderItemsTable(auditDetail.missingItems, 'missing')}
                {activeTab === 'extra' && renderItemsTable(auditDetail.extraItems, 'extra')}
              </div>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditDetailModal;
